"use client";

import {
  type FC,
  type ReactNode,
  type RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ButtonToggle,
  Flex,
  List,
  Message,
  Placeholder,
  Stack,
  Text,
  useUuid,
  ValueParser,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { rem } from "@huub-web/utils";

import { InfoIcon } from "../InfoIcon";
import {
  mortgageInterestRatePeriodOptions,
  type MortgageInterestRate,
  type MortgageInterestRatePeriod,
} from "../../types/MortgageInterestRate";

import {
  interestRatesSimpleImageStyle,
  rateRowListStyle,
  rateRowContentStyle,
  rateRowCtaStyle,
} from "./InterestRatesSimple.css";

export type InterestRatesSimpleBankImageCallback = (
  rate: MortgageInterestRate,
) => ReactNode;

export type InterestRatesSimpleFilterValues = {
  rentevastePeriod: MortgageInterestRatePeriod;
  nhg: boolean;
};

export type InterestRatesSimpleHandle = {
  /**
   * Imperatively update filter values from outside the component.
   */
  updateFilterValues: (
    values: Partial<InterestRatesSimpleFilterValues>,
  ) => void;
};

export type InterestRatesSimpleProps = {
  cta?: ReactNode;
  limit?: number;
  hasPartner?: boolean;
  /**
   * Initial fixed-rate period selected when the component mounts.
   * Defaults to "20" when not provided.
   */
  defaultPeriod?: MortgageInterestRatePeriod;
  /**
   * Initial NHG (Nationale Hypotheek Garantie) toggle state when the component mounts.
   * Defaults to true ("Met") when not provided.
   */
  defaultNhg?: boolean;
  getInterestRates: (
    period: MortgageInterestRatePeriod,
    nhg: boolean,
    limit: number,
  ) => Promise<MortgageInterestRate[]>;
  onLoadingChange?: (loading: boolean) => void;
  /**
   * Callback fired after interest rates are successfully loaded with the
   * current filter values. Deduplicated to only fire when values change.
   */
  onFiltersChange?: (filters: {
    rentevastePeriod: string;
    nhg: string;
  }) => void;
  getBankImage?: InterestRatesSimpleBankImageCallback;
  /**
   * Ref to imperatively update filter values from a parent template.
   */
  filterRef?: RefObject<InterestRatesSimpleHandle | null>;
};

/**
 * InterestRatesSimple component displays a simple list of interest rates.
 * @todo Remove maxMortgageWizard.result from i18n keys
 */
export const InterestRatesSimple: FC<InterestRatesSimpleProps> = ({
  getInterestRates,
  onLoadingChange,
  onFiltersChange,
  cta,
  hasPartner = false,
  limit = 3,
  defaultPeriod = "20",
  defaultNhg = true,
  getBankImage,
  filterRef,
}) => {
  const { t } = useTranslation();

  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const lastReportedFiltersRef = useRef<string>("");

  const uuid = useUuid();
  const [rentevastePeriod, setRentevastePeriod] =
    useState<MortgageInterestRatePeriod>(defaultPeriod);
  const [nhg, setNhg] = useState<boolean>(defaultNhg);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [interestRates, setInterestRates] = useState<MortgageInterestRate[]>(
    [],
  );

  useImperativeHandle(
    filterRef,
    () => ({
      updateFilterValues: (values) => {
        if (values.rentevastePeriod !== undefined) {
          setRentevastePeriod(values.rentevastePeriod);
        }
        if (values.nhg !== undefined) {
          setNhg(values.nhg);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    setLoading(true);
    setError(false);

    getInterestRates(rentevastePeriod, nhg, limit)
      .then((rates) => {
        setInterestRates(rates.slice(0, limit));
        const filterKey = JSON.stringify({ rentevastePeriod, nhg });
        if (filterKey !== lastReportedFiltersRef.current) {
          lastReportedFiltersRef.current = filterKey;
          onFiltersChangeRef.current?.({
            rentevastePeriod,
            nhg: String(nhg),
          });
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [rentevastePeriod, nhg, limit, getInterestRates]);

  return (
    <Flex direction="column" gap="m">
      <Flex gap="m" wrap justify="space-between">
        <Flex direction="column" gap="s">
          <Text as="label" htmlFor={`rentevastePeriod-${uuid}`} bold>
            {t("maxMortgageWizard.result.interestRates.fixedRatePeriod.label")}{" "}
            <InfoIcon
              value={t(
                `maxMortgageWizard.result.interestRates.fixedRatePeriod.info.${hasPartner ? "plural" : "singular"}`,
              )}
            />
          </Text>
          <ButtonToggle
            id={`rentevastePeriod-${uuid}`}
            disabled={loading || !!error}
            options={mortgageInterestRatePeriodOptions.map((option) => ({
              value: option.value,
              label: t("interestRatesSimple.year", {
                count: Number(option.label),
              }),
            }))}
            value={rentevastePeriod}
            onChange={(value) =>
              setRentevastePeriod(value as MortgageInterestRatePeriod)
            }
          />
        </Flex>

        <Flex direction="column" gap="s" align="flex-end">
          <Text as="label" htmlFor={`nhg-${uuid}`} bold>
            {t("maxMortgageWizard.result.interestRates.nhg.label")}{" "}
            <InfoIcon
              value={t(
                `maxMortgageWizard.result.interestRates.nhg.info.${hasPartner ? "plural" : "singular"}`,
              )}
            />
          </Text>
          <ButtonToggle
            id={`nhg-${uuid}`}
            disabled={loading || !!error}
            options={[
              { value: "true", label: t("interestRatesSimple.nhg.with") },
              { value: "false", label: t("interestRatesSimple.nhg.without") },
            ]}
            value={nhg ? "true" : "false"}
            onChange={(value) => setNhg(value === "true")}
          />
        </Flex>
      </Flex>

      <Stack reveal>
        {error && !loading && (
          <Message variant="error" body={t("interestRatesSimple.error")} />
        )}
        {!error && interestRates.length > 0 && (
          <List variant="secondary" className={rateRowListStyle}>
            {interestRates.map((item, i) => (
              <Flex key={i} wrap align="center" gap="s" padding="xs">
                {getBankImage ? (
                  getBankImage(item)
                ) : (
                  <img
                    src={item.image}
                    alt={`${item.name} logo`}
                    className={interestRatesSimpleImageStyle}
                  />
                )}
                <Flex className={rateRowContentStyle} align="center" gap="s">
                  <Text grow>{item.productName ?? item.name}</Text>
                  <Text bold noWrap shrink={false}>
                    {!item.rate || item.rate === 0 ? (
                      "—"
                    ) : (
                      <ValueParser
                        value={{ value: item.rate, type: "percentage" }}
                      />
                    )}
                  </Text>
                </Flex>
                <div className={rateRowCtaStyle}>{cta}</div>
              </Flex>
            ))}
          </List>
        )}
        {loading && interestRates.length === 0 && (
          <Flex gap="s" direction="column">
            <Placeholder height={rem(48)} />
            <Placeholder height={rem(48)} />
            <Placeholder height={rem(48)} />
          </Flex>
        )}
      </Stack>
    </Flex>
  );
};
